import React, { useState, useCallback } from 'react';
import { useTranslation } from 'localization';
import ReviewCardMolecule, { type ReviewCardAuthor } from './ReviewCardMolecule';

const INITIAL_DISPLAY_COUNT = 3;

export interface ReviewItem {
  id: string;
  stars: number;
  title?: string | null;
  body: string;
  authorName?: string | null;
  author?: ReviewCardAuthor | null;
  date: string;
  isEdited?: boolean;
}

export interface ReviewListMoleculeProps {
  reviews: ReviewItem[];
  totalCount: number;
  onShowAll?: () => void;
  className?: string;
}

const ReviewListMolecule: React.FC<ReviewListMoleculeProps> = ({
  reviews,
  totalCount,
  onShowAll,
  className = '',
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleShowAll = useCallback(() => {
    if (onShowAll) {
      onShowAll();
    } else {
      setExpanded(true);
    }
  }, [onShowAll]);

  const classes = ['marketplace-review-list', className].filter(Boolean).join(' ');

  if (reviews.length === 0) {
    return (
      <div className={classes}>
        <h3 className="marketplace-review-list__title">
          {t('marketplace.reviews.title')}
        </h3>
        <p className="marketplace-review-list__empty">
          {t('marketplace.reviews.noReviews')}
        </p>
      </div>
    );
  }

  const displayedReviews = expanded
    ? reviews
    : reviews.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = !expanded && totalCount > INITIAL_DISPLAY_COUNT;

  return (
    <div className={classes}>
      <h3 className="marketplace-review-list__title">
        {t('marketplace.reviews.title')}
      </h3>
      <div className="marketplace-review-list__items">
        {displayedReviews.map((review) => (
          <ReviewCardMolecule
            key={review.id}
            stars={review.stars}
            title={review.title}
            body={review.body}
            authorName={review.authorName}
            author={review.author}
            date={review.date}
            isEdited={review.isEdited}
          />
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          className="marketplace-review-list__show-all"
          onClick={handleShowAll}
        >
          {t('marketplace.reviews.showAll', { count: totalCount })}
        </button>
      )}
    </div>
  );
};

export default ReviewListMolecule;
